<template>
  <div>
    <div v-if="thisTag">
      <HeaderBar>
        <v-icon v-if="thisTag.segmentType === 'state'" slot="icon">
          location_on
        </v-icon>
        <v-icon v-if="thisTag.segmentType === 'region'" slot="icon">
          public
        </v-icon>
        {{ thisTag.name }} ({{ thisTag.segmentType }})
        <template slot="buttons">
          <router-link :to="{ name: 'hrGeotags' }">
            Back to Geotags
          </router-link>
        </template>
      </HeaderBar>

      <v-layout row justify-space-between class="my-2">
        <v-flex class="text-xs-right">
          <v-btn
            color="primary"
            :to="{ name: 'geoReport', params: { tagIDs: thisTag.id } }"
          >
            Report
          </v-btn>
        </v-flex>
      </v-layout>

      <div v-if="thisTag.segmentType === 'region'" class="mb-4">
        <h2 class="mb-2">States in this region</h2>
        <div v-if="user.bohOrHigher">
          <v-layout row justify-space-between>
            <v-flex>
              <v-select
                multiple
                chips
                deletable-chips
                v-model="memberStateIds"
                :items="stateTags"
                item-value="id"
                item-text="name"
                label="Member states"
              ></v-select>
            </v-flex>
            <v-flex>
              <v-btn color="success" :loading="savingMembers" @click="saveMemberStates">
                Save States
              </v-btn>
            </v-flex>
          </v-layout>
        </div>
        <div v-else>
          {{ (thisTag.memberStates || []).map(s => s.name).join(", ") || "None" }}
        </div>
      </div>

      <h2 class="mb-2">Locations</h2>

      <v-layout row justify-space-between>
        <v-flex>
          <v-autocomplete
            multiple
            v-model="worksiteIDs"
            :items="validLocations"
            :label="`Add Locations to ${thisTag.name}`"
            item-value="id"
            item-text="name"
            :search-input.sync="locationSearcher"
            @click="getLocations"
            :loading="loadingLocations"
          >
          </v-autocomplete>
        </v-flex>
        <v-flex>
          <v-btn color="success" @click="addTag(thisTag.id)"> Add </v-btn>
        </v-flex>
      </v-layout>

      <v-layout column>
        <v-flex class="mb-4">
          <div v-if="!thisTag.worksites || thisTag.worksites.length === 0">
            No locations in this tag
          </div>

          <div v-else>
            <v-data-table
              :headers="headers"
              :items="thisTag.worksites"
              :pagination.sync="defaultSort"
              class="elevation-1"
            >
              <template slot="items" slot-scope="{ item }">
                <tr>
                  <td class="text-xs-left">
                    <router-link
                      :to="{
                        name: 'worksiteShow',
                        params: { worksiteId: item.id }
                      }"
                      target="_blank"
                    >
                      {{ item.name }}
                    </router-link>
                  </td>
                  <td class="text-xs-right">
                    <div v-if="thisTag.segmentType === 'state'">
                      <div v-if="regionsOnly(item.segments).length > 0">
                        <GeoTag
                          :key="t.id"
                          class="mb-2"
                          :geotag="t"
                          v-for="t in regionsOnly(item.segments)"
                        />
                      </div>
                      <div v-else>-</div>
                    </div>

                    <div v-if="thisTag.segmentType === 'region'">
                      <div v-if="statesOnly(item.segments).length > 0">
                        <GeoTag
                          :key="t.id"
                          class="mb-2"
                          :geotag="t"
                          v-for="t in statesOnly(item.segments)"
                        />
                      </div>
                      <div v-else>-</div>
                    </div>
                  </td>
                  <td class="text-xs-right">
                    <v-icon @click="removeTag(item.id, thisTag.id)" small
                      >delete</v-icon
                    >
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-flex>

        <v-card>
          <v-card-title>
            <v-flex v-if="thisTag.segmentType === 'state'">
              <h2>Employees</h2>

              <v-layout row justify-space-between>
                <v-flex>
                  <v-autocomplete
                    multiple
                    :items="addableEmployees"
                    v-model="employeesToAdd"
                    item-text="name"
                    :loading="employeesLoading"
                    :disabled="employeesLoading"
                    clearable
                    item-value="id"
                    :label="`Add Employees to ${thisTag.name}`"
                  >
                  </v-autocomplete>
                </v-flex>
                <v-flex>
                  <v-btn color="success" @click="addEmployee()"> Add </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="`Search ${thisTag.name} employees`"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>

          <v-data-table
            :headers="employeeHeaders"
            :items="addedEmployees"
            :pagination.sync="defaultSort"
            class="elevation-0"
            item-key="id"
            :search="search"
          >
            <template slot="items" slot-scope="{ item }">
              <tr>
                <td>
                  <router-link
                    :to="{
                      name: 'employeeDetail',
                      params: { employeeId: item.id }
                    }"
                    target="_blank"
                  >
                    {{ item.display_name }}
                  </router-link>
                </td>
                <td style="text-align: right">
                  <v-icon @click="removeEmployee(item.id)" small>delete</v-icon>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import GeoTag from "@/components/geotags/geo-tag.vue";
import collectionsApi from "@/api/collections";
import geotags from "@/api/geotags";

export default {
  created() {
    this.setUp();
  },

  data() {
    return {
      employeeHeaders: [
        // TODO
        { text: "Name", value: "name" }
        // { text: "" },
      ],
      search: "",

      allEmployees: [],
      employeesLoading: false,
      employeesToAdd: [],

      locations: [],
      loadingLocations: false,
      worksiteIDs: [],
      locationSearcher: null,
      newRegionName: "",
      newStateName: "",
      pending: false,
      tagID: 0,
      memberStateIds: [],
      savingMembers: false,
      headers: [
        { text: "Location", value: "location" },
        { text: "" },
        { text: "" }
      ],
      defaultSort: {
        sortBy: "location",
        ascending: true,
        rowsPerPage: 10
      }
    };
  },

  watch: {
    locationSearcher(value) {
      // this triggers when anything is entered into the clients autocomplete
      this.getLocations();
    },
    thisTag: {
      immediate: true,
      handler(tag) {
        if (tag && tag.memberStates) {
          this.memberStateIds = tag.memberStates.map(s => s.id);
        }
      }
    }
  },

  computed: {
    ...mapState("geotags", ["geotags"]),
    ...mapGetters("geotags", [
      "tagByID",
      "stateTags",
      "regionTags",
      "defaultTags"
    ]),
    ...mapState("userAuth", ["user"]),

    addedEmployees() {
      if (
        !this.thisTag.worksites.length ||
        this.thisTag.worksites.length === 0
      ) {
        return [];
      }
      return this.allEmployees.filter(e => {
        return !this.addableEmployees.includes(e);
      });
    },

    addableEmployees() {
      if (!this.allEmployees || this.allEmployees.length === 0) {
        return [];
      }
      return this.allEmployees.filter(employee => {
        return !employee.geotag_states.includes(this.thisTag.name);
      });
    },
    employeesOnList() {
      if (!this.addedEmployees) {
        return [];
      }
      return this.addedEmployees;
    },

    thisTag() {
      if (this.tagID && this.geotags) {
        return this.tagByID(this.tagID);
      }
      return this.tagByID(this.tagID);
    },

    validLocations() {
      return this.locations.filter(l => {
        return !this.thisTag.worksites.map(w => w.id).includes(l.id);
      });
    }
  },

  methods: {
    saveMemberStates() {
      this.savingMembers = true;
      this.$store
        .dispatch("geotags/updateMembers", {
          tagID: this.thisTag.id,
          memberSegmentIDs: this.memberStateIds
        })
        .finally(() => {
          this.savingMembers = false;
        });
    },

    setUp() {
      this.$store.dispatch("geotags/getAll");
      this.tagID = parseInt(this.$route.params.tagID);

      this.employeesLoading = true;
      collectionsApi.getEmployees(employees => {
        this.allEmployees = employees;
        this.employeesLoading = false;
      });
    },

    regionsOnly(segments) {
      return segments.filter(s => {
        return s.segment_type === "region";
      });
    },

    removeTag(worksiteID, tagID) {
      const confirmRemove = confirm("Are you sure you want to remove?");
      if (confirmRemove) {
        this.$store.dispatch("geotags/removeWorksiteTag", {
          worksiteID,
          tagID
        });
      }
    },

    addTag(tagID) {
      this.$store.dispatch("geotags/addWorksitesTag", {
        worksiteIDs: this.worksiteIDs,
        tagID
      });
      this.worksiteIDs = [];
    },

    addEmployee() {
      geotags.addEmployee(
        { employeeIDs: this.employeesToAdd, tagID: this.tagID },
        () => {
          this.setUp();
        }
      );
    },

    removeEmployee(employeeID) {
      const confirmRemove = confirm("Are you sure you want to remove?");
      if (confirmRemove) {
        geotags.removeEmployee(
          { employeeID: employeeID, tagID: this.tagID },
          () => {
            this.setUp();
          }
        );
      }
    },

    statesOnly(segments) {
      return segments.filter(s => {
        return s.segment_type === "state";
      });
    },

    getLocations() {
      if (this.locations.length > 0) {
        // already got them
        return;
      }
      if (this.loadingLocations) {
        // already started getting them
        return;
      }
      this.loadingLocations = true;

      // get Locations
      collectionsApi.getLocations(
        locations => {
          this.locations = locations;
          this.loadingLocations = false;
        },
        error => {
          this.loadingLocations = false;
        }
      );
    }
  },

  components: {
    GeoTag
  }
};
</script>
